<template>
  <div @submit.prevent>
    <!-- ════════════════════════════════════════════════════
         FORMULARIO DE PAGO
    ════════════════════════════════════════════════════ -->
    <div v-if="!props.mostrarTerminosStripe">
      <!-- Header Stripe -->
      <div class="stripe-header">
        <img
          src="https://cdn.worldvectorlogo.com/logos/stripe-4.svg"
          alt="Stripe"
          width="60"
        />
        <span class="stripe-desc">
          Pagos 100% seguros gestionados por <strong>Stripe</strong>
          <i class="bi bi-shield-lock-fill text-success ms-1"></i>
        </span>
      </div>

      <!-- Precio calculado -->
      <div v-if="precioInscripcion" class="precio-badge">
        <span class="precio-label">Total a pagar</span>
        <span class="precio-monto">{{ formatPrecio(precioInscripcion.precio) }}</span>
        <span class="precio-desc">
          {{ labelModalidad(precioInscripcion.modalidad_combate) }} ·
          {{ labelGrupoEdad(precioInscripcion.grupo_edad) }}
        </span>
      </div>
      <div v-else-if="cargandoPrecio" class="precio-badge precio-loading">
        <div class="spinner-sm"></div>
        <span>Calculando precio…</span>
      </div>
      <div v-else class="precio-badge precio-error">
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>No hay tarifa disponible para tu modalidad</span>
      </div>

      <h6 class="card-titulo">Datos de tu tarjeta</h6>

      <!-- Stripe Elements -->
      <div class="stripe-fields">
        <div class="stripe-field-wrap">
          <div id="card-element" class="stripe-element"></div>
        </div>
      </div>

      <div id="card-errors" class="card-errors-msg"></div>

      <p class="stripe-nota">
        ¿Dudas sobre cómo Stripe protege tu información?
        <a href="#" @click.prevent="$emit('toggle-terminos-stripe', true)">Conoce más</a>
      </p>

      <!-- Navegación -->
      <div class="nav-row">
        <button class="btn-nav btn-secondary-nav" @click="$emit('prev-step')">
          <i class="bi bi-arrow-left"></i>
          <span>Atrás</span>
        </button>
        <a href="/" class="btn-nav btn-ghost-nav">
          <i class="bi bi-house-fill"></i>
          <span>Inicio</span>
        </a>
        <button
          class="btn-nav btn-primary-nav"
          type="button"
          :disabled="!stripeLoaded || !precioInscripcion"
          @click="verificarStripeYMostrarResumen"
        >
          <span>Ver resumen</span>
          <i class="bi bi-eye"></i>
        </button>
      </div>

      <!-- ── Modal Resumen ── -->
      <transition name="fade">
        <div
          v-if="mostrarResumen"
          class="resumen-overlay"
          @click.self="mostrarResumen = false"
        >
          <div class="resumen-box">
            <h4 class="resumen-titulo">Resumen de inscripción</h4>

            <div class="resumen-seccion">
              <p class="resumen-row">
                <span><i class="bi bi-person"></i> Nombre</span>
                <strong>{{ store.nombre }} {{ store.apellido }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-envelope"></i> Correo</span
                ><strong>{{ store.email }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-phone"></i> Teléfono</span
                ><strong>{{ store.telefono }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-geo-alt"></i> Dirección</span
                ><strong>{{ store.direccion }}</strong>
              </p>
            </div>

            <div class="resumen-seccion">
              <p class="resumen-row">
                <span><i class="bi bi-calendar"></i> Fecha de nac.</span
                ><strong>{{ store.fechaNacimiento }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-gender-ambiguous"></i> Género</span
                ><strong>{{ store.genero === "H" ? "Masculino" : "Femenino" }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-award"></i> Cinta</span
                ><strong>{{ store.rangoCinta }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-lightning"></i> Modalidad</span
                ><strong>{{ labelModalidad(store.matchPreference) }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-people"></i> Academia</span
                ><strong>{{ store.academia }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-diagram-3"></i> Categoría</span
                ><strong>{{ labelCategoria(store.categoriaPeso) }}</strong>
              </p>
            </div>

            <div class="resumen-total">
              <span>Total</span>
              <strong>{{
                precioInscripcion ? formatPrecio(precioInscripcion.precio) : "—"
              }}</strong>
            </div>

            <div class="resumen-nav">
              <button
                class="btn-nav btn-secondary-nav"
                @click.stop="mostrarResumen = false"
              >
                Cancelar
              </button>
              <button class="btn-nav btn-success-nav" @click="confirmarInscripcion">
                <i class="bi bi-check-circle-fill"></i>
                Confirmar inscripción
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- ════════════════════════════════════════════════════
         TÉRMINOS STRIPE
    ════════════════════════════════════════════════════ -->
    <div v-else>
      <TerminosYCondicionesStripe />
      <div class="text-end mt-3">
        <button
          class="btn-nav btn-secondary-nav"
          @click="$emit('toggle-terminos-stripe', false)"
        >
          <i class="bi bi-arrow-left"></i> Volver al formulario de pago
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";

import TerminosYCondicionesStripe from "@/components/Torneo/TerminosYCondicionesStripe.vue";
import { useInscripcionTours } from "@/store/inscripcionTours";
import {
  getTorneoActivo,
  getPrecioInscripcion,
  iniciarPagoInscripcion,
  subirFotoPeleador,
  verificarPagoInscripcion,
} from "@/utils/api_backend";
import { getFromIndexedDB, deleteFromIndexedDB } from "@/utils/indexedDB";

const store = useInscripcionTours();
const router = useRouter();

const isProcessing = ref(false);
const mostrarResumen = ref(false);
const stripeLoaded = ref(false);
const precioInscripcion = ref(null);
const cargandoPrecio = ref(true);

let stripe, elements, cardElement;

const props = defineProps({ mostrarTerminosStripe: Boolean });

// ── Helpers de display ────────────────────────────────────────────────────
const formatPrecio = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v);

const labelModalidad = (m) => ({ gi: "Con Gi", nogi: "No-Gi" }[m] ?? m);
const labelGrupoEdad = (g) => ({ adulto: "Adulto", juvenil: "Juvenil" }[g] ?? g);

const labelCategoria = (cat) => {
  if (!cat) return "—";
  const nombres = {
    rooster: "Rooster",
    light_feather: "Light Feather",
    feather: "Feather",
    light: "Light",
    middle: "Middle",
    medium_heavy: "Medium Heavy",
    heavy: "Heavy",
    super_heavy: "Super Heavy",
    ultra_heavy: "Ultra Heavy",
  };
  const div = nombres[cat.division] ?? cat.division;
  const peso = cat.peso_max_kg ? `≤ ${cat.peso_max_kg} kg` : "Open";
  return `${div} — ${peso}`;
};

function calcularGrupoEdad() {
  if (!store.fechaNacimiento) return "adulto";
  const hoy = new Date();
  const nac = new Date(store.fechaNacimiento);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad < 18 ? "juvenil" : "adulto";
}

// ── Lifecycle ─────────────────────────────────────────────────────────────
onMounted(async () => {
  stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
  elements = stripe.elements();

  const style = {
    base: {
      color: "#32325d",
      fontFamily: "Roboto, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#a0aec0" },
    },
    invalid: { color: "#e3342f", iconColor: "#e3342f" },
  };

  cardElement = elements.create("card", { style, hidePostalCode: true });
  cardElement.mount("#card-element");
  stripeLoaded.value = true;

  // Calcular precio para mostrar el badge antes de confirmar
  try {
    cargandoPrecio.value = true;
    const torneo = await getTorneoActivo();
    if (!torneo) return;

    const precios = await getPrecioInscripcion(torneo.id);
    const grupoEdad = calcularGrupoEdad();
    const modalidad = store.matchPreference;

    precioInscripcion.value =
      precios.find(
        (p) => p.modalidad_combate === modalidad && p.grupo_edad === grupoEdad
      ) ?? null;

    if (!precioInscripcion.value) {
      Swal.fire({
        icon: "warning",
        title: "Precio no disponible",
        text: `No hay tarifa para ${labelModalidad(modalidad)} / ${labelGrupoEdad(
          grupoEdad
        )}.`,
        confirmButtonColor: "#007bff",
      });
    }
  } catch (err) {
    console.error("Error obteniendo precio:", err);
  } finally {
    cargandoPrecio.value = false;
  }
});

// ── Validación Stripe ─────────────────────────────────────────────────────
function validarCamposStripe() {
  if (!cardElement._complete) {
    Swal.fire({
      icon: "warning",
      title: "Campos incompletos",
      text: "Completa los datos de tu tarjeta.",
    });
    return false;
  }
  return true;
}

function verificarStripeYMostrarResumen() {
  if (!precioInscripcion.value) {
    Swal.fire({
      icon: "warning",
      title: "Precio no disponible",
      text: "No se pudo calcular el precio.",
    });
    return;
  }
  if (validarCamposStripe()) mostrarResumen.value = true;
}

// ── Confirmar inscripción + pago — NUEVO FLUJO ────────────────────────────
async function confirmarInscripcion() {
  mostrarResumen.value = false;
  isProcessing.value = true;

  try {
    Swal.fire({
      title: "Conectando con el banco…",
      text: "Por favor, no cierres esta ventana.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    // 1. Foto desde IndexedDB
    const file = await getFromIndexedDB("peleadores", "fotoPerfil");
    if (!file) {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "Falta tu foto",
        text: "Ingresa tu imagen de perfil.",
      });
      return;
    }

    // 2. Torneo activo
    const torneo = await getTorneoActivo();
    if (!torneo) {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "Sin torneo activo",
        text: "No hay torneo disponible.",
      });
      return;
    }

    // 3. Un solo POST — crea RegistroPendiente + PaymentIntent
    let pagoData;
    try {
      pagoData = await iniciarPagoInscripcion({
        torneoId: torneo.id,
        currency: "mxn",
        peleador: {
          nombre: store.nombre,
          apellido: store.apellido,
          email: store.email.trim().toLowerCase(),
          telefono: store.telefono,
          nacionalidad: store.nacionalidad,
          direccion: store.direccion,
          fecha_nacimiento: store.fechaNacimiento,
          genero: store.genero,
          redes_sociales: {
            instagram: store.redesSociales.instagram || null,
            facebook: store.redesSociales.facebook || null,
            youtube: store.redesSociales.youtube || null,
            twitter: store.redesSociales.twitter || null,
          },
        },
        inscripcion: {
          modalidad_combate: store.matchPreference,
          cinta: store.rangoCinta,
          equipo: store.academia,
          categoria_id: store.categoriaPeso?.id,
          rol: "Competidor",
        },
      });
    } catch (error) {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "Error al iniciar el pago",
        text: extraerMensajeError(error),
        confirmButtonColor: "#007bff",
      });
      return;
    }

    // 4. Confirmar pago con Stripe
    const result = await stripe.confirmCardPayment(pagoData.clientSecret, {
      payment_method: { card: cardElement },
    });

    Swal.close();

    if (result.error) {
      await Swal.fire({
        icon: "error",
        title: "Pago rechazado",
        text: result.error.message,
        confirmButtonColor: "#dc3545",
      });
      return;
    }

    if (result.paymentIntent.status === "succeeded") {
      // 5. Verificar que el webhook procesó correctamente — polling con reintentos
      Swal.fire({
        title: "Confirmando inscripción…",
        text: "Estamos verificando tu pago, un momento.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      let verificado = false;
      const MAX_INTENTOS = 6;
      const ESPERA_MS = 2000; // 2s entre intentos → máx 12s

      for (let i = 0; i < MAX_INTENTOS; i++) {
        await new Promise((r) => setTimeout(r, ESPERA_MS));
        try {
          const resultado = await verificarPagoInscripcion(result.paymentIntent.id);
          if (resultado.verificado) {
            verificado = true;
            break;
          }
        } catch (_) {
          // Reintentar silenciosamente
        }
      }

      Swal.close();

      // 6. Webhook no confirmó en tiempo — advertencia, no error
      if (!verificado) {
        await Swal.fire({
          icon: "warning",
          title: "Pago recibido, confirmando inscripción",
          html: `
            <p>Tu pago fue procesado correctamente.</p>
            <p>Estamos terminando de confirmar tu inscripción.</p>
            <p>Recibirás un correo en <strong>${store.email}</strong> en los próximos minutos.</p>
            <p style="font-size:0.82rem;color:#aaa">Si no recibes nada en 10 minutos, contáctanos.</p>
          `,
          confirmButtonText: "Entendido",
          confirmButtonColor: "#007bff",
          allowOutsideClick: false,
        });
        store.resetFormulario();
        await deleteFromIndexedDB("peleadores", "fotoPerfil");
        router.push("/");
        return;
      }

      // 7. Webhook confirmado — subir foto
      let fotoOk = true;
      try {
        await subirFotoPeleador(store.email.trim().toLowerCase(), file);
      } catch (fotoError) {
        fotoOk = false;
        await Swal.fire({
          icon: "warning",
          title: "Inscripción confirmada, pero hubo un problema con tu foto",
          text:
            "Tu inscripción fue exitosa, pero no pudimos subir tu foto de perfil. Por favor, contáctanos para resolverlo.",
          confirmButtonColor: "#007bff",
        });
      } finally {
        await deleteFromIndexedDB("peleadores", "fotoPerfil");
      }

      // 8. Todo OK — solo si la foto no mostró ya un Swal
      if (fotoOk) {
        await Swal.fire({
          icon: "success",
          title: "¡Inscripción exitosa!",
          html: `
            <p>Tu inscripción se ha realizado exitosamente.</p>
            <p>Recibirás la confirmación en <strong>${store.email}</strong>.</p>
          `,
          confirmButtonText: "Ir al inicio",
          confirmButtonColor: "#007bff",
          allowOutsideClick: false,
        });
      }

      store.resetFormulario();
      router.push("/");
    }
  } catch (error) {
    Swal.close();
    await Swal.fire({
      icon: "error",
      title: "Error inesperado",
      text: extraerMensajeError(error),
      confirmButtonColor: "#dc3545",
    });
  } finally {
    isProcessing.value = false;
  }
}

function extraerMensajeError(error) {
  const data = error?.response?.data;
  if (!data) return error.message || "Error desconocido.";
  if (Array.isArray(data)) return data[0];
  if (typeof data === "object") {
    const camposPrioritarios = ["error", "errores", "detail", "non_field_errors"];
    for (const campo of camposPrioritarios) {
      if (data[campo]) {
        const val = data[campo];
        return Array.isArray(val) ? val[0] : val;
      }
    }
    const primerCampo = Object.values(data)[0];
    return Array.isArray(primerCampo) ? primerCampo[0] : String(primerCampo);
  }
  return String(data);
}
</script>
<style scoped>
/* ── Stripe header ── */
.stripe-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.stripe-desc {
  font-size: 0.82rem;
  color: #666;
}

/* ── Badge de precio ── */
.precio-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #f0f7ff;
  border: 1px solid #c8e0ff;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
}
.precio-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.precio-monto {
  font-size: 1.5rem;
  font-weight: 800;
  color: #007bff;
}
.precio-desc {
  font-size: 0.78rem;
  color: #888;
  margin-left: auto;
}
.precio-loading {
  background: #f8f8f8;
  border-color: #e0e0e0;
  color: #999;
  font-size: 0.85rem;
}
.precio-error {
  background: #fff8f0;
  border-color: #ffd0a0;
  color: #c04000;
  font-size: 0.85rem;
  gap: 8px;
}

/* ── Stripe fields ── */
.card-titulo {
  font-weight: 700;
  color: #333;
  margin-bottom: 14px;
  font-size: 0.95rem;
}

.stripe-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 6px;
}
.stripe-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stripe-field-wrap {
  position: relative;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  padding: 10px 12px 10px 10px;
  transition: border-color 0.2s;
}
.stripe-field-wrap:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}
.stripe-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
  font-size: 0.95rem;
}
.stripe-element {
  min-height: 22px;
}

.card-errors-msg {
  color: #dc3545;
  font-size: 0.8rem;
  min-height: 20px;
  margin-bottom: 4px;
}

.stripe-nota {
  font-size: 0.78rem;
  color: #999;
  text-align: right;
  margin-top: 10px;
}
.stripe-nota a {
  color: #007bff;
}

/* ── Navegación ── */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-primary-nav:disabled {
  background: #b0c8f0;
  cursor: not-allowed;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}
.btn-ghost-nav {
  background: transparent;
  color: #888;
  border: 1.5px solid #ddd;
}
.btn-ghost-nav:hover {
  border-color: #bbb;
  color: #555;
}
.btn-success-nav {
  background: linear-gradient(135deg, #28a745, #1e8035);
  color: #fff;
  font-weight: 700;
}
.btn-success-nav:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(40, 167, 69, 0.3);
}

/* ── Spinner pequeño ── */
.spinner-sm {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(0, 123, 255, 0.2);
  border-top-color: #007bff;
  animation: giro 0.8s linear infinite;
  flex-shrink: 0;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

/* ── Modal resumen ── */
.resumen-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.52);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.resumen-box {
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  border-radius: 20px;
  padding: 2.2rem;
  max-width: 500px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;

  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 170, 255, 0.08);
}
.resumen-titulo {
  font-weight: 900;
  font-size: 1.2rem;
  color: #0f172a;
  margin-bottom: 1.5rem;
  padding-bottom: 14px;

  border-bottom: 2px solid transparent;
  background: linear-gradient(90deg, #00aaff, #007bff) bottom;
  background-size: 100% 2px;
  background-repeat: no-repeat;

  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
}
.resumen-seccion {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e2e8f0;
}
.resumen-seccion:last-of-type {
  border-bottom: none;
}
.resumen-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 0 8px;
  font-size: 0.88rem;
}

.resumen-row span {
  color: #64748b;
  font-weight: 500;
}

.resumen-row strong {
  color: #0f172a;
  font-weight: 700;
}
.resumen-total {
  display: flex;
  justify-content: space-between;
  align-items: center;

  background: linear-gradient(135deg, #00aaff, #007bff);
  color: white;

  border-radius: 12px;
  padding: 8px 18px;
  margin: 10px 0;
}

.resumen-total span {
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.85;
}

.resumen-total strong {
  font-size: 1.6rem;
  font-weight: 900;
  color: #fff;
}
.resumen-nav {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 20px;
}

/* ── Fade ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .stripe-row-2 {
    grid-template-columns: 1fr;
  }
  .precio-desc {
    margin-left: 0;
  }
}
</style>
